/**
 * Language-aware content loader for policy documents
 * Loads content based on the current language setting, falling back to English if not available
 */

// Import all English content
import privacyPolicyEn from '../content/en/privacyPolicy.json';
import authorizedRepresentativesEn from '../content/en/authorizedRepresentatives.json';
import financialResponsibilityEn from '../content/en/financialResponsibility.json';
import imagerySocialMediaEn from '../content/en/imagerySocialMedia.json';
import imageryTreatmentEducationEn from '../content/en/imageryTreatmentEducation.json';
import medicalRecordsReleaseEn from '../content/en/medicalRecordsRelease.json';
import noticeOfPrivacyPracticesEn from '../content/en/noticeOfPrivacyPractices.json';
import recordsReleaseEn from '../content/en/recordsRelease.json';
import selfPayFinancialPolicyEn from '../content/en/selfPayFinancialPolicy.json';

// Import all Spanish content
import privacyPolicyEs from '../content/es/privacyPolicy.json';
import authorizedRepresentativesEs from '../content/es/authorizedRepresentatives.json';
import financialResponsibilityEs from '../content/es/financialResponsibility.json';
import imagerySocialMediaEs from '../content/es/imagerySocialMedia.json';
import imageryTreatmentEducationEs from '../content/es/imageryTreatmentEducation.json';
import medicalRecordsReleaseEs from '../content/es/medicalRecordsRelease.json';
import noticeOfPrivacyPracticesEs from '../content/es/noticeOfPrivacyPractices.json';
import recordsReleaseEs from '../content/es/recordsRelease.json';
import selfPayFinancialPolicyEs from '../content/es/selfPayFinancialPolicy.json';

// Content maps by language
const contentMaps = {
  en: {
    privacyPolicy: privacyPolicyEn,
    authorizedRepresentatives: authorizedRepresentativesEn,
    financialResponsibility: financialResponsibilityEn,
    imagerySocialMedia: imagerySocialMediaEn,
    imageryTreatmentEducation: imageryTreatmentEducationEn,
    medicalRecordsRelease: medicalRecordsReleaseEn,
    noticeOfPrivacyPractices: noticeOfPrivacyPracticesEn,
    recordsRelease: recordsReleaseEn,
    selfPayFinancialPolicy: selfPayFinancialPolicyEn,
  },
  es: {
    privacyPolicy: privacyPolicyEs,
    authorizedRepresentatives: authorizedRepresentativesEs,
    financialResponsibility: financialResponsibilityEs,
    imagerySocialMedia: imagerySocialMediaEs,
    imageryTreatmentEducation: imageryTreatmentEducationEs,
    medicalRecordsRelease: medicalRecordsReleaseEs,
    noticeOfPrivacyPractices: noticeOfPrivacyPracticesEs,
    recordsRelease: recordsReleaseEs,
    selfPayFinancialPolicy: selfPayFinancialPolicyEs,
  },
};

// Fallback content (for backwards compatibility)
const fallbackContent = {
  privacyPolicy: privacyPolicyEn,
  authorizedRepresentatives: authorizedRepresentativesEn,
  financialResponsibility: financialResponsibilityEn,
  imagerySocialMedia: imagerySocialMediaEn,
  imageryTreatmentEducation: imageryTreatmentEducationEn,
  medicalRecordsRelease: medicalRecordsReleaseEn,
  noticeOfPrivacyPractices: noticeOfPrivacyPracticesEn,
  recordsRelease: recordsReleaseEn,
  selfPayFinancialPolicy: selfPayFinancialPolicyEn,
};

/**
 * Get content for a specific content type based on language
 * @param {string} contentName - The name of the content to load (e.g., 'privacyPolicy')
 * @param {string} language - The language code (e.g., 'en', 'es')
 * @returns {object} The content object
 */
export function getContent(contentName, language = 'en') {
  // Normalize language code (handle cases like 'en-US' -> 'en')
  const langCode = language.split('-')[0].toLowerCase();
  
  // Try to get content for the requested language
  if (contentMaps[langCode] && contentMaps[langCode][contentName]) {
    return contentMaps[langCode][contentName];
  }
  
  // Fall back to English
  if (contentMaps.en && contentMaps.en[contentName]) {
    return contentMaps.en[contentName];
  }
  
  // Final fallback
  return fallbackContent[contentName] || null;
}

/**
 * React hook for loading language-aware content
 * Usage: const privacyPolicy = useContent('privacyPolicy');
 */
export function useContent(contentName) {
  // This is a simple synchronous loader since we're using static imports
  // The actual language detection happens at call time
  return (language) => getContent(contentName, language);
}

/**
 * Get all available content names
 * @returns {string[]} Array of content names
 */
export function getAvailableContent() {
  return Object.keys(contentMaps.en);
}

/**
 * Check if content is available in a specific language
 * @param {string} contentName - The content name
 * @param {string} language - The language code
 * @returns {boolean} True if content exists for the language
 */
export function isContentAvailable(contentName, language) {
  const langCode = language.split('-')[0].toLowerCase();
  return !!(contentMaps[langCode] && contentMaps[langCode][contentName]);
}

export default {
  getContent,
  useContent,
  getAvailableContent,
  isContentAvailable,
};
